import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../../../../environments/environment';
import { CompaniesService } from '../../../../services/companies';
import { ResigteringService } from '../../../../services/resigtering.service';

@Component({
  selector: 'app-myprofilecompany',
  templateUrl: './myprofilecompany.component.html',
  styleUrl: './myprofilecompany.component.scss'
})
export class MyprofilecompanyComponent implements OnInit {
  company: any = null;
  companyForm: FormGroup;
  logoFailed = false;
  isLoading = true;
  isEditing = false;
  isSaving = false;
  submitted = false;
  errorMessage = '';
  successMessage = '';

  private readonly storageRoot = environment.Server_URL
    .replace(/\/api\/?$/, '')
    .replace(/\/$/, '');
  private isBrowser = false;

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private userService: ResigteringService,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.companyForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      name_ar: ['', [Validators.maxLength(255)]],
      specialty: ['', [Validators.maxLength(255)]],
      industry: ['', [Validators.maxLength(255)]],
      tel: ['', [Validators.required, Validators.maxLength(30)]],
      fax: ['', [Validators.maxLength(50)]],
      contact_email: ['', [Validators.email, Validators.maxLength(255)]],
      address: ['', [Validators.maxLength(1000)]],
      facebook: ['', [Validators.maxLength(255)]],
      linkedin: ['', [Validators.maxLength(255)]],
    });
  }

  ngOnInit(): void {
    const companyId = this.getCompanyIdFromStorage();

    if (!companyId) {
      this.loadCompanyFromCurrentUser();
      return;
    }

    this.loadCompany(companyId);
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang().startsWith('ar');
  }

  get companyName(): string {
    return (
      this.pickLang(this.company, 'name_ar', 'name_en', 'name') ||
      this.company?.company_name ||
      this.emptyText
    );
  }

  get companyInitial(): string {
    const source = this.companyName || this.company?.contact_email || '';
    return source.trim().charAt(0).toUpperCase() || '?';
  }

  get logoUrl(): string {
    if (this.logoFailed) {
      return '';
    }

    return this.getCompanyLogoUrl(this.company);
  }

  get emptyText(): string {
    return this.isAr ? 'غير محدد' : 'Not specified';
  }

  get mainInfoItems(): Array<{ label: string; value: any; link?: string }> {
    return [
      {
        label: this.isAr ? 'اسم الشركة' : 'Company Name',
        value: this.companyName,
      },
      {
        label: this.isAr ? 'التخصص' : 'Specialty',
        value: this.company?.specialty,
      },
      {
        label: this.isAr ? 'المجال' : 'Industry',
        value: this.company?.industry,
      },
      {
        label: this.isAr ? 'الهاتف' : 'Phone',
        value: this.company?.tel || this.company?.phone || this.company?.mobile,
        link: this.company?.tel ? `tel:${this.company.tel}` : undefined,
      },
      {
        label: this.isAr ? 'الفاكس' : 'Fax',
        value: this.company?.fax,
      },
      {
        label: this.isAr ? 'البريد الإلكتروني' : 'Contact Email',
        value: this.company?.contact_email || this.company?.email,
        link: this.company?.contact_email
          ? `mailto:${this.company.contact_email}`
          : undefined,
      },
    ];
  }

  get locationItems(): Array<{ label: string; value: any }> {
    return [
      {
        label: this.isAr ? 'الدولة' : 'Country',
        value:
          this.pickLang(this.company?.country, 'country_name_ar', 'country_name_en', 'name') ||
          this.pickLang(this.company, 'country_name_ar', 'country_name_en', 'country_name') ||
          this.company?.country_id,
      },
      {
        label: this.isAr ? 'المدينة' : 'City',
        value:
          this.pickLang(this.company?.city, 'city_name_ar', 'city_name_en', 'name') ||
          this.pickLang(this.company, 'city_name_ar', 'city_name_en', 'city_name') ||
          this.company?.city_id,
      },
      {
        label: this.isAr ? 'العنوان' : 'Address',
        value: this.company?.address,
      },
    ];
  }

  get socialItems(): Array<{ label: string; value: any; link?: string }> {
    return [
      {
        label: 'Facebook',
        value: this.company?.facebook,
        link: this.normalizeExternalLink(this.company?.facebook),
      },
      {
        label: 'LinkedIn',
        value: this.company?.linkedin,
        link: this.normalizeExternalLink(this.company?.linkedin),
      },
      {
        label: this.isAr ? 'الموقع الإلكتروني' : 'Website',
        value: this.company?.website || this.company?.url,
        link: this.normalizeExternalLink(this.company?.website || this.company?.url),
      },
    ];
  }

  get statusItems(): Array<{ label: string; value: any }> {
    return [
      {
        label: this.isAr ? 'عميل أحمد الشحات' : 'Ahmed El-Shahat Client',
        value: this.booleanLabel(this.company?.is_ahmed_elshahat_client),
      },
      {
        label: this.isAr ? 'شركة مرتبطة بحساب' : 'Linked User Company',
        value: this.booleanLabel(this.company?.is_user),
      },
      {
        label: this.isAr ? 'تاريخ الإنشاء' : 'Created At',
        value: this.formatDate(this.company?.created_at),
      },
      {
        label: this.isAr ? 'آخر تحديث' : 'Updated At',
        value: this.formatDate(this.company?.updated_at),
      },
    ];
  }

  get additionalItems(): Array<{ label: string; value: any }> {
    if (!this.company) {
      return [];
    }

    const excluded = new Set([
      'id',
      'name',
      'name_ar',
      'name_en',
      'company_name',
      'specialty',
      'industry',
      'tel',
      'phone',
      'mobile',
      'fax',
      'contact_email',
      'email',
      'country',
      'country_id',
      'country_name',
      'country_name_ar',
      'country_name_en',
      'city',
      'city_id',
      'city_name',
      'city_name_ar',
      'city_name_en',
      'address',
      'facebook',
      'linkedin',
      'website',
      'url',
      'logo',
      'logo_url',
      'company_logo',
      'image',
      'photo',
      'is_user',
      'is_ahmed_elshahat_client',
      'created_at',
      'updated_at',
      'deleted_at',
    ]);

    return Object.entries(this.company)
      .filter(([key, value]) => {
        return !excluded.has(key) && this.hasDisplayValue(value);
      })
      .map(([key, value]) => ({
        label: this.humanizeKey(key),
        value: this.formatValue(value),
      }));
  }

  trackByLabel(_: number, item: { label: string }): string {
    return item.label;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }

  displayValue(value: any): string {
    const formatted = this.formatValue(value);
    return formatted || this.emptyText;
  }

  startEditing(): void {
    if (!this.company) {
      return;
    }

    this.successMessage = '';
    this.errorMessage = '';
    this.submitted = false;
    this.companyForm.reset({
      name: this.company.name || this.company.name_en || this.company.company_name || '',
      name_ar: this.company.name_ar || '',
      specialty: this.company.specialty || '',
      industry: this.company.industry || '',
      tel: this.company.tel || this.company.phone || this.company.mobile || '',
      fax: this.company.fax || '',
      contact_email: this.company.contact_email || this.company.email || '',
      address: this.company.address || '',
      facebook: this.company.facebook || '',
      linkedin: this.company.linkedin || '',
    });
    this.isEditing = true;
  }

  cancelEditing(): void {
    this.isEditing = false;
    this.submitted = false;
    this.companyForm.reset();
  }

  isInvalid(controlName: string): boolean {
    const control = this.companyForm.get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.touched || control.dirty || this.submitted)
    );
  }

  saveCompanyData(): void {
    this.submitted = true;
    this.successMessage = '';
    this.errorMessage = '';

    if (this.companyForm.invalid || !this.company?.id) {
      this.companyForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    const payload = this.buildCompanyUpdateFormData();

    this.companiesService
      .update(Number(this.company.id), payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (res: any) => {
          const updatedCompany = this.normalizeCompanyResponse(res);

          this.company = {
            ...this.company,
            ...this.companyForm.value,
            ...(updatedCompany || {}),
          };

          this.isEditing = false;
          this.submitted = false;
          this.successMessage = this.isAr
            ? 'تم تحديث بيانات الشركة بنجاح'
            : 'Company data updated successfully';

          Swal.fire({
            icon: 'success',
            title: this.successMessage,
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
        error: (err: any) => {
          const message =
            err?.error?.message ||
            (this.isAr
              ? 'تعذر تحديث بيانات الشركة'
              : 'Unable to update company data');

          this.errorMessage = message;

          Swal.fire({
            icon: 'error',
            title: message,
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
  }

  private buildCompanyUpdateFormData(): FormData {
    const formData = new FormData();
    const value = this.companyForm.getRawValue();

    [
      'name',
      'name_ar',
      'specialty',
      'industry',
      'tel',
      'fax',
      'contact_email',
      'address',
      'facebook',
      'linkedin',
    ].forEach((key) => {
      const fieldValue = value[key];

      if (
        fieldValue !== null &&
        fieldValue !== undefined &&
        String(fieldValue).trim() !== ''
      ) {
        formData.append(key, String(fieldValue).trim());
      }
    });

    const countryId = this.company?.country_id || this.company?.country?.id;
    const cityId = this.company?.city_id || this.company?.city?.id;

    if (countryId) {
      formData.append('country_id', String(countryId));
    }

    if (cityId) {
      formData.append('city_id', String(cityId));
      formData.append('cities_id', String(cityId));
    }

    formData.append('is_user', this.toBackendBoolean(this.company?.is_user ?? true));
    formData.append(
      'is_ahmed_elshahat_client',
      this.toBackendBoolean(this.company?.is_ahmed_elshahat_client ?? false)
    );

    return formData;
  }

  private loadCompany(companyId: number | string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.companiesService.getById(Number(companyId)).subscribe({
      next: (res: any) => {
        this.company = this.normalizeCompanyResponse(res);
        this.logoFailed = false;
        this.isLoading = false;

        if (!this.company) {
          this.handleError(
            this.isAr
              ? 'لم يتم العثور على بيانات الشركة'
              : 'Company data was not found'
          );
        }
      },
      error: (err: any) => {
        this.handleError(
          err?.error?.message ||
            (this.isAr
              ? 'تعذر تحميل بيانات الشركة'
              : 'Unable to load company data')
        );
      },
    });
  }

  private loadCompanyFromCurrentUser(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const storedUserId = this.getUserIdFromStorage();

    if (storedUserId) {
      this.loadCompanyFromUserId(storedUserId);
      return;
    }

    this.userService.getUserId().subscribe({
      next: (res: any) => {
        const userId = res?.user_id || res?.id;

        if (!userId) {
          this.handleMissingCompanyId();
          return;
        }

        this.loadCompanyFromUserId(userId);
      },
      error: () => this.handleMissingCompanyId(),
    });
  }

  private loadCompanyFromUserId(userId: number | string): void {
    this.userService.getUserById(userId).subscribe({
      next: (user: any) => {
        const companyId = user?.company_id || user?.company?.id;

        if (companyId) {
          localStorage.setItem('company_id', String(companyId));
          this.loadCompany(companyId);
          return;
        }

        if (user?.company) {
          this.company = user.company;
          this.logoFailed = false;
          this.isLoading = false;
          return;
        }

        this.handleMissingCompanyId();
      },
      error: () => this.handleMissingCompanyId(),
    });
  }

  private handleMissingCompanyId(): void {
    this.handleError(
      this.isAr
        ? 'لم يتم العثور على رقم الشركة لهذا الحساب'
        : 'Company id was not found for this account'
    );
  }

  private normalizeCompanyResponse(res: any): any {
    return res?.data?.data || res?.data || res?.company || res || null;
  }

  private getCompanyIdFromStorage(): number | string | null {
    if (!this.isBrowser) {
      return null;
    }

    const directCompanyId = localStorage.getItem('company_id');

    if (directCompanyId) {
      return directCompanyId;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      return user?.company_id || user?.company?.id || null;
    } catch {
      return null;
    }
  }

  private getUserIdFromStorage(): number | string | null {
    if (!this.isBrowser) {
      return null;
    }

    const directUserId = localStorage.getItem('user_id');

    if (directUserId) {
      return directUserId;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      return user?.id || null;
    } catch {
      return null;
    }
  }

  private pickLang(
    source: any,
    arKey: string,
    enKey: string,
    fallbackKey?: string
  ): string {
    if (!source) {
      return '';
    }

    const primary = this.isAr ? source?.[arKey] : source?.[enKey];
    const secondary = this.isAr ? source?.[enKey] : source?.[arKey];

    return primary || secondary || (fallbackKey ? source?.[fallbackKey] : '') || '';
  }

  private getCompanyLogoUrl(company: any): string {
    const imageUrl =
      company?.image_url ||
      company?.logo_url ||
      company?.full_image_url ||
      company?.photo_url;

    const image =
      company?.image ||
      company?.logo ||
      company?.company_logo ||
      company?.photo;

    if (imageUrl) {
      return imageUrl;
    }

    if (!image) {
      return '';
    }

    const imageValue = String(image);

    if (imageValue.startsWith('http://') || imageValue.startsWith('https://')) {
      return imageValue;
    }

    const cleanImage = imageValue.replace(/^\/+/, '');

    if (cleanImage.startsWith('storage/')) {
      return `${this.storageRoot}/${cleanImage}`;
    }

    return `${this.storageRoot}/storage/${cleanImage}`;
  }

  private normalizeExternalLink(value?: string | null): string | undefined {
    if (!value) {
      return undefined;
    }

    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  }

  private booleanLabel(value: any): string {
    const isTruthy = value === true || value === 1 || value === '1';
    return isTruthy ? (this.isAr ? 'نعم' : 'Yes') : this.isAr ? 'لا' : 'No';
  }

  private formatDate(dateValue?: string | null): string {
    if (!dateValue) {
      return '';
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(this.isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  private formatValue(value: any): string {
    if (!this.hasDisplayValue(value)) {
      return '';
    }

    if (typeof value === 'boolean') {
      return this.booleanLabel(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.formatValue(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
      return (
        this.pickLang(value, 'name_ar', 'name_en', 'name') ||
        this.pickLang(value, 'title_ar', 'title_en', 'title') ||
        JSON.stringify(value)
      );
    }

    return String(value);
  }

  private hasDisplayValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private toBackendBoolean(value: any): string {
    return value === true || value === 1 || value === '1' || value === 'true'
      ? '1'
      : '0';
  }

  private humanizeKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.isLoading = false;
  }

}
